// The whole front end of a second business's deployment (see scripts/tenant-build.js).
//
// src/index.js mounts this INSTEAD of App when REACT_APP_MODULES is set, and because that
// test is a build-time constant webpack drops App — and every Hollypaul page, driver list
// and route file it imports — from their bundle entirely. So nothing here may import from
// App, Layout, AppContext or navConfig: that would pull our engine back into their build.
//
// A page is only mounted when its module is switched on; everything else redirects to the
// first enabled page. Turning a module on later = add it to HP_MODULES and redeploy.
//
// Pages shared with Hollypaul read useApp() from context/appContextCore (the bare context,
// not the provider). TenantProvider below fills it with the little those pages need.
import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink, Outlet, useSearchParams } from 'react-router-dom';
import { FileUp, TrendingUp, Lock, Eye, EyeOff, LogOut, Menu, X, Upload, Search, Layers, FileSignature } from 'lucide-react';
import ErrorBoundary from '../components/ErrorBoundary';
import WahrImportPage from '../pages/WahrImportPage';
import WahrComparisonPage from '../pages/WahrComparisonPage';
import ImportParcelsPage from '../pages/ImportParcelsPage';
import ParcelAuditPage from '../pages/ParcelAuditPage';
import DailySnapshotPage from '../pages/DailySnapshotPage';
import AgreementsPage from '../pages/AgreementsPage';
import { AppContext } from '../context/appContextCore';
import { detectPeriodEnd, todayISO, PERIOD_ANCHOR_END } from '../utils/periods';
import { TENANT_MODULES, BUSINESS_NAME } from './config';
import '../styles/global.css';

// Every page a tenant can have, keyed by module. Order = sidebar order.
const PAGES = {
  wahr: [
    { path: '/wahr-import',     label: 'Import WAHR',     icon: FileUp,     element: <WahrImportPage /> },
    { path: '/wahr-comparison', label: 'WAHR Comparison', icon: TrendingUp, element: <WahrComparisonPage /> },
  ],
  parcels: [
    { path: '/import-parcels',  label: 'Import Parcels',  icon: Upload,     element: <ImportParcelsPage /> },
    { path: '/parcel-audit',    label: 'Parcel Audit',    icon: Search,     element: <ParcelAuditPage /> },
    { path: '/daily-snapshot',  label: 'Daily Snapshot',  icon: Layers,     element: <DailySnapshotPage /> },
  ],
  agreements: [
    { path: '/agreements',      label: 'Agreements',      icon: FileSignature, element: <AgreementsPage /> },
  ],
};

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// What the shared pages read from useApp(). Hollypaul's comes from the Google Sheet; here:
// - the pay period is AP's 28-day grid (same anchor as ours), current period from today.
//   Import Parcels reads it as Controls' reportEndDate, "DD-Mon-YYYY".
// - personnel = their drivers table, so Import Parcels can flag a login nobody has added.
// - everyone signed in is an admin of their own site (HP_ADMIN_USERS narrows the server side).
function TenantProvider({ children }) {
  const currentEnd = useMemo(() => detectPeriodEnd(todayISO(), PERIOD_ANCHOR_END), []);
  const [activePeriodISO, setActivePeriodISO] = useState(currentEnd);
  const [personnel, setPersonnel] = useState([]);
  useEffect(() => {
    if (!TENANT_MODULES.includes('parcels')) return;
    fetch('/.netlify/functions/parcel-audit-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'listDrivers' }) })
      .then((r) => r.json())
      .then((d) => { if (d && d.success) setPersonnel(d.drivers.map((x) => ({ loginName: x.login, name: x.name }))); })
      .catch(() => {});
  }, []);
  const value = useMemo(() => {
    const [y, m, d] = currentEnd.split('-');
    return {
      currentUser: { role: 'admin' },
      sheetData: { controls: { reportEndDate: `${d}-${MON[parseInt(m, 10) - 1]}-${y}` } },
      sheetLoading: false,
      activePeriodISO, setActivePeriodISO,
      personnel,
      addPendingOnboarding: () => {},
    };
  }, [currentEnd, activePeriodISO, personnel]);
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

const pages = TENANT_MODULES.flatMap((m) => PAGES[m] || []);
const home = pages[0] ? pages[0].path : '/login';

// Sign-in messages, keyed by tenant-auth's ?error= code.
const LOGIN_ERRORS = {
  '1': 'Login ID or PIN not recognised.',
  locked: 'Too many wrong tries. Wait 15 minutes, then try again.',
  expired: 'Your temporary PIN has expired. Ask for a new one.',
  reset: 'That step timed out. Sign in again with your temporary PIN.',
  server: 'Something went wrong. Please try again.',
};

function TenantLogin() {
  const [params] = useSearchParams();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const redirect = params.get('redirect') || home;
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--hp-black)' }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <Lock size={28} color="var(--hp-gold)" />
          <h1 style={{ fontSize: '1.6rem', margin: '12px 0 4px', color: 'var(--hp-text)' }}>{BUSINESS_NAME}</h1>
          <p style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', margin: 0 }}>Sign in</p>
        </div>
        <form action="/.netlify/functions/tenant-auth" method="POST" className="card" style={{ padding: 24 }}>
          <input type="hidden" name="action" value="login" />
          <input type="hidden" name="redirect" value={redirect} />
          {params.get('error') && (
            <div style={{ marginBottom: 14, fontSize: '0.8rem', color: 'var(--hp-red)' }}>{LOGIN_ERRORS[params.get('error')] || LOGIN_ERRORS['1']}</div>
          )}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label">Login ID</label>
            <input name="login" className="form-input" value={login} onChange={(e) => setLogin(e.target.value.toUpperCase())} autoComplete="username" autoCapitalize="characters" required autoFocus />
          </div>
          <div className="form-group" style={{ marginBottom: 18 }}>
            <label className="form-label">PIN</label>
            <div style={{ position: 'relative' }}>
              <input type={show ? 'text' : 'password'} name="password" className="form-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required style={{ paddingRight: 40 }} />
              <button type="button" onClick={() => setShow(!show)} aria-label={show ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}>
                {show ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Sign in</button>
        </form>
      </div>
    </div>
  );
}

// First sign-in with a temporary PIN lands here (tenant-auth sets a 15-minute hp_reset cookie
// and issues no session until a new PIN is saved).
function TenantSetPin() {
  const [params] = useSearchParams();
  const [show, setShow] = useState(false);
  const type = show ? 'text' : 'password';
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--hp-black)' }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 22 }}>
          <Lock size={28} color="var(--hp-gold)" />
          <h1 style={{ fontSize: '1.4rem', margin: '12px 0 6px', color: 'var(--hp-text)' }}>Choose your PIN</h1>
          <p style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', margin: 0, lineHeight: 1.5 }}>
            You signed in with a temporary PIN. Set your own before you continue. We suggest the same PIN as your scanner login.
          </p>
        </div>
        <form action="/.netlify/functions/tenant-auth" method="POST" className="card" style={{ padding: 24 }}>
          <input type="hidden" name="action" value="setpin" />
          {params.get('error') && (
            <div style={{ marginBottom: 14, fontSize: '0.8rem', color: 'var(--hp-red)' }}>{params.get('error')}</div>
          )}
          <div className="form-group" style={{ marginBottom: 14 }}>
            <label className="form-label">New PIN</label>
            <input type={type} name="pin" className="form-input" autoComplete="new-password" inputMode="numeric" minLength={4} required autoFocus />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label className="form-label">Type it again</label>
            <input type={type} name="pin2" className="form-input" autoComplete="new-password" inputMode="numeric" minLength={4} required />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 18, cursor: 'pointer' }}>
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Show PIN
          </label>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Save and continue</button>
        </form>
      </div>
    </div>
  );
}

function TenantLayout() {
  const [open, setOpen] = useState(false);
  // The session cookie is HttpOnly, so only the edge gate can clear it (/logout).
  const signOut = () => { window.location.href = '/logout'; };
  const nav = (
    <>
      <div className="sidebar-brand">
        <div className="brand-logo" style={{ fontSize: '1.1rem', lineHeight: 1.2 }}>{BUSINESS_NAME}</div>
      </div>
      <nav className="sidebar-nav">
        {pages.map(({ path, label, icon: Icon }) => (
          <NavLink key={path} to={path} onClick={() => setOpen(false)} className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>
            <Icon /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-footer">
        <button type="button" onClick={signOut} className="nav-link" style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer' }}>
          <LogOut /> Sign out
        </button>
      </div>
    </>
  );
  return (
    <div className="app-shell">
      <aside className="sidebar sidebar-desktop">{nav}</aside>
      <div className="mobile-topbar">
        <button onClick={() => setOpen(true)} className="mobile-menu-btn" aria-label="Open menu"><Menu size={22} /></button>
        <span className="mobile-brand">{BUSINESS_NAME}</span>
        <span />
      </div>
      {open && <div className="mobile-overlay" onClick={() => setOpen(false)} />}
      <aside className={`sidebar sidebar-mobile${open ? ' open' : ''}`}>
        <button onClick={() => setOpen(false)} className="mobile-close-btn" aria-label="Close menu"><X size={20} /></button>
        {nav}
      </aside>
      <main className="main-content">
        <ErrorBoundary label="tenant"><Outlet /></ErrorBoundary>
      </main>
    </div>
  );
}

export default function TenantApp() {
  return (
    <TenantProvider>
    <Router>
      <Routes>
        <Route path="/login" element={<TenantLogin />} />
        <Route path="/set-pin" element={<TenantSetPin />} />
        <Route path="/" element={<TenantLayout />}>
          {pages.map(({ path, element }) => (
            <Route key={path} path={path.slice(1)} element={element} />
          ))}
          <Route index element={<Navigate to={home} replace />} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Route>
      </Routes>
    </Router>
    </TenantProvider>
  );
}
